/**
 * Team names for one league, keyed by teamId. The Squares reads return team
 * ids only, and the create flow has no pool yet, so this resolves the league
 * from its key through the league catalog, then lists its teams. The same
 * two hops `features/pick/useTeamNames.ts` makes, minus the pool read.
 */
import {
  useLeagueCatalogGet,
  useTeamList,
} from "@sportsmet/sports-sdk/private";
import { useMemo } from "react";

const CATALOG_STALE_TIME_MS = 5 * 60 * 1000;

export type typeSquaresTeam = {
  teamId: string;
  name: string;
  abbreviation: string;
};

export type typeSquaresLeagueTeams = {
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
  /** Sorted by name, for the team filter. */
  teams: readonly typeSquaresTeam[];
  leagueName: string;
  isLoading: boolean;
};

export function useSquaresLeagueTeams(
  leagueKey: string | null,
): typeSquaresLeagueTeams {
  const catalog = useLeagueCatalogGet({}, { staleTime: CATALOG_STALE_TIME_MS });

  const league = useMemo(() => {
    if (leagueKey === null) return null;
    for (const sport of catalog.data?.data?.sports ?? []) {
      const found = sport.leagues.find(
        (candidate) => candidate.leagueKey === leagueKey,
      );
      if (found) return found;
    }
    return null;
  }, [catalog.data, leagueKey]);

  const leagueId = league?.leagueId ?? "";
  const teamList = useTeamList(
    { leagueId },
    { enabled: leagueId !== "", staleTime: CATALOG_STALE_TIME_MS },
  );

  return useMemo(() => {
    const teams = (teamList.data?.data?.teams ?? [])
      .map((team) => ({
        teamId: team.teamId,
        name: team.name,
        abbreviation: team.abbreviation,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "en-US"));
    const teamsById = new Map<string, typeSquaresTeam>();
    for (const team of teams) teamsById.set(team.teamId, team);
    return {
      teamsById,
      teams,
      leagueName: league?.shortName ?? league?.name ?? "league",
      isLoading: catalog.isLoading || (leagueId !== "" && teamList.isLoading),
    };
  }, [teamList.data, teamList.isLoading, catalog.isLoading, league, leagueId]);
}
